/**
 * Relative date formatting utility
 *
 * Formats an ISO 8601 timestamp as a human-readable relative date string.
 * Returns "—" for invalid date strings.
 *
 * Requirements: 3.2
 */

/**
 * Format an ISO 8601 timestamp as a human-readable relative date.
 * @param isoString - An ISO 8601 date string
 * @returns A relative date string (e.g., "just now", "5 minutes ago", "2 hours ago")
 *          or "—" if the input is invalid
 */
export function formatRelativeDate(isoString: string): string {
  if (!isoString || typeof isoString !== 'string') {
    return '—';
  }

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    return '—';
  }

  const now = Date.now();
  const diffMs = now - date.getTime();

  // Future dates or negative diffs — treat as invalid
  if (diffMs < 0) {
    return '—';
  }

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) {
    return 'just now';
  }
  if (minutes < 60) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  }
  if (hours < 24) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (days < 7) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }
  if (weeks < 5 && months < 1) {
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  if (months < 12) {
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }
  return years === 1 ? '1 year ago' : `${years} years ago`;
}
